import { API, downloadSignedUrl } from '../common/api';
import { FIELD_URL } from '../common/env';
import { Journey } from '../../pages/customer-profile/types/GrowerService';
import {
  DCEventTypes,
  EventsSuccess,
  IDCEventType,
  IDCHarvestEvent,
  IDCPlantingEvent,
  IDCTillageEvent,
  IEvents,
  IEventsToDelete,
  IPracticeImplementationToUpdate,
} from '../../pages/customer-profile/components/Tabs/DataCollectionTab/interfaces';
import { v4 as uuidv4 } from 'uuid';
import { createNewEvent, ErroredEventMapping, getDateText } from '../../pages/customer-profile/components/Tabs/DataCollectionTab/utils/utils';
import { Field } from '../../pages/customer-profile/types/Field';
import { cloneDeep } from 'lodash';
import { currentDateISO } from '../../pages/customer-profile/utils/utils';
import AgoToast from '../../components/Toast/AgoToast';
import { DATE_CONSTRAINTS } from '../../pages/customer-profile/components/Tabs/DataCollectionTab/common/Constants';

const getEventsConfiguration = async (journeyId: string): Promise<{ events: DCEventTypes } | void> => {
  return await API.get(`${FIELD_URL}/field-service/data-collection/${journeyId}`)
    .then((res) => {
      const events = {} as DCEventTypes;
      res.data.forEach((eventTypes) => {
        events[eventTypes.name] = {
          ...eventTypes,
          events: {},
        };
        eventTypes.events.forEach((event) => {
          events[eventTypes.name].events[event.id] = getEventWithoutNullValues(event);
          events[eventTypes.name].events[event.id].erroredEvent = ErroredEventMapping[eventTypes.name](event);
        });
      });

      const noPlantingHarvestIds: string[] = Object.keys(events['Harvest'].events).filter((harvestId) => {
        return !Object.keys(events['Planting'].events).find(
          (plantingId) => (events['Harvest'].events[harvestId] as IDCHarvestEvent).plantingId === plantingId,
        );
      });
      const oldPlantingIds = Array.from(
        new Set(noPlantingHarvestIds.map((harvestId) => (events['Harvest'].events[harvestId] as IDCHarvestEvent).plantingId)),
      );

      for (const oldPlantingId of oldPlantingIds) {
        const harvestIds = noPlantingHarvestIds.filter(
          (harvestId) => oldPlantingId === (events['Harvest'].events[harvestId] as IDCHarvestEvent).plantingId,
        );
        const newEvent = createNewEvent((events['Harvest'].events[harvestIds[0]] as IDCHarvestEvent).fieldId, 'Planting') as IDCPlantingEvent;
        harvestIds.forEach((harvestId) => {
          (events['Harvest'].events[harvestId] as IDCHarvestEvent).plantingId = newEvent.id;
        });
        events['Planting'].events[newEvent.id] = newEvent;
      }

      return { events };
    })
    .catch(() => {
      throw Error('Error while retrieving events');
    });
};

const getImplementationYear = async (journeyId: string): Promise<{ journeyId: string; opportunityId: string; implementationYear: number } | void> => {
  return await API.get(`${FIELD_URL}/field-service/data-collection/implementation-year/${journeyId}`)
    .then((res) => {
      return res.data;
    })
    .catch(() => {
      throw Error('Error while retrieving implementation year');
    });
};

const getFarmInformation = async (
  journeyId: string,
): Promise<{
  opportunityId: string;
  journeyId: string;
  implementationYear: Record<string, number | undefined>;
  grazingSeason: { startDate: Date; endDate: Date };
} | void> => {
  return await API.get(`${FIELD_URL}/field-service/data-collection/farm-information/${journeyId}`)
    .then((res) => {
      return res.data;
    })
    .catch(() => {
      throw Error('Error while retrieving farm information');
    });
};

const upsertImplementationYear = async (body: {
  journeyId: string;
  opportunityId: string;
  implementationYear: number;
  practiceType: 'ROW_CROP' | 'PASTURE';
}): Promise<{ journeyId: string; opportunityId: string; implementationYear: number } | void> => {
  return await API.post(`${FIELD_URL}/field-service/data-collection/implementation-year`, body)
    .then((res) => {
      return res.data;
    })
    .catch(() => {
      throw Error('Error while retrieving implementation year');
    });
};

const upsertGrazingSeason = async (body: {
  journeyId: string;
  opportunityId: string;
  startDate: Date;
  endDate: Date;
}): Promise<{ journeyId: string; opportunityId: string; startDate: Date; endDate: Date } | void> => {
  return await API.post(`${FIELD_URL}/field-service/data-collection/grazing-season`, body)
    .then((res) => {
      return res.data;
    })
    .catch(() => {
      throw Error('Error while retrieving farm information');
    });
};

const getStateLocationRelations = async (): Promise<Array<{
  state: string;
  stateCode: string;
  location: string;
}> | void> => {
  return await API.get(`${FIELD_URL}/field-service/data-collection/state-locations`)
    .then((res) => {
      return res.data;
    })
    .catch(() => {
      throw Error('Error while retrieving state location relations');
    });
};

const getAllAffectedEvents = (eventTypes: DCEventTypes, editedEvents: DCEventTypes) => {
  const eventTypesCopy = cloneDeep(eventTypes);
  const fieldsAffected = new Set<string>();
  for (const eventType of Object.values(editedEvents)) {
    for (const updatedEvent of Object.values(eventType.events)) {
      eventTypesCopy[eventType.name].events[updatedEvent.id] = updatedEvent;
      if (eventType.name !== 'AnimalData') {
        fieldsAffected.add((updatedEvent as any).fieldId);
      }
    }
  }

  const allAffectedEvents: DCEventTypes = cloneDeep(editedEvents);

  for (const eventType of Object.values(eventTypesCopy) as IDCEventType[]) {
    for (const event of Object.values(eventType.events)) {
      if (eventType.name === 'AnimalData' || fieldsAffected.has((event as any).fieldId)) {
        allAffectedEvents[eventType.name].events[event.id] = event;
      }
    }
  }

  return allAffectedEvents;
};

const treatedEventTypes = (eventTypes, filterNewEvents = false) => {
  const eventTypesCopy = JSON.parse(JSON.stringify(eventTypes));
  for (const eventType of Object.values(eventTypesCopy) as IDCEventType[]) {
    for (const event of Object.values(eventType.events)) {
      if (filterNewEvents && event.newEvent) {
        delete eventType.events[event.id];
      } else {
        delete event.createdAt;
        delete event.updatedAt;
        delete event.newEvent; //Remove flag used for display purposes
      }
    }
  }
  return eventTypesCopy;
};

const copyEventTypes = (eventTypes: DCEventTypes, eventsTypesEdited: DCEventTypes, fieldIds: string[]) => {
  const eventTypesCopy = JSON.parse(JSON.stringify(eventTypes));
  for (const eventTypeEdited of Object.values(eventsTypesEdited) as IDCEventType[]) {
    const originalEventType = (Object.values(eventTypesCopy) as IDCEventType[]).find((evType) => evType.name === eventTypeEdited.name);

    for (const event of Object.values(eventTypeEdited.events)) {
      if (event.copy) {
        fieldIds.forEach((fieldId) => {
          const id = uuidv4();
          originalEventType!.events[id] = {
            ...event,
            id,
            fieldId: eventTypeEdited.name !== 'AnimalData' ? fieldId : '',
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          for (const dateParam of DATE_CONSTRAINTS) {
            originalEventType!.events[id][dateParam] = undefined;
          }

          if (originalEventType!.name === 'Planting') {
            const harvestEventType = eventTypesCopy.Harvest as IDCEventType;
            const harvestEventValues = Object.values(harvestEventType.events) as IDCHarvestEvent[];
            if (harvestEventValues.length) {
              harvestEventValues
                .filter((e) => e.plantingId === event.id)
                .forEach((he) => {
                  const hId = uuidv4();
                  harvestEventType.events[hId] = {
                    ...he,
                    id: hId,
                    fieldId,
                    plantingId: id,
                    harvestDate: '',
                    createdAt: new Date(),
                    updatedAt: new Date(),
                  };
                });
            }
          }
        });
      }
    }
  }

  const allAffectedEvents = {};
  for (const eventType of Object.values(eventTypesCopy) as IDCEventType[]) {
    allAffectedEvents[eventType.name] = allAffectedEvents[eventType.name] ?? { ...eventType, events: {} };
    for (const event of Object.values(eventType.events)) {
      if (eventType.name === 'AnimalData' || fieldIds.includes((event as any).fieldId)) {
        allAffectedEvents[eventType.name].events[event.id] = event;
      }
    }
  }

  return { allEvents: eventTypesCopy, copiedEvents: allAffectedEvents };
};

const saveEvents = async (producerEvents: DCEventTypes, journey: Journey, eventsDeleted: IEventsToDelete[] = [], eventFieldId: string) => {
  const allEvents = Object.values(producerEvents).map((eventType) => {
    const eventTypeDeleted = eventsDeleted.find((e) => e.name === eventType.name)?.eventIds ?? [];
    const events = Object.values(eventType.events).filter((e) => !eventTypeDeleted.includes(e.id) && (!eventFieldId || e.fieldId === eventFieldId));
    return {
      ...eventType,
      constraints: null,
      events,
    };
  });

  const eventsFlat = Object.values(allEvents)
    .map((eventType) => eventType.events)
    .flat();
  const eventsAmount = eventsFlat.length;

  if (eventsAmount < 200) {
    return await API.post(`${FIELD_URL}/field-service/data-collection`, {
      journeyId: journey.id,
      opportunityId: journey.opportunityId,
      producerEvents: allEvents,
    }).then((res) => {
      const savedEvents: EventsSuccess = res.data.eventsSuccess;
      for (const eventType of Object.keys(savedEvents)) {
        for (const event of Object.values(savedEvents[eventType]) as IEvents[]) {
          event.erroredEvent = ErroredEventMapping[eventType](event);
          savedEvents[eventType][event.id] = getEventWithoutNullValues(event);
        }
      }
      return res;
    });
  }

  const fieldIds = Array.from(new Set(eventsFlat.map((event) => event.fieldId).filter((id) => !!id)));

  let finalStatus: number | undefined;
  const result: any = { eventsSuccess: {}, eventsFailed: {} };
  const batchSize = 3;
  let promises: Promise<void>[] = [];

  for (const fieldId of fieldIds) {
    const currentFieldEvents = Object.values(allEvents).map((eventType) => {
      const events = Object.values(eventType.events).filter((e) => e.fieldId === fieldId);
      return {
        ...eventType,
        constraints: null,
        events,
      };
    });

    promises.push(
      upserEvents(journey.id, journey.opportunityId, currentFieldEvents, result).then((status) => {
        if (!finalStatus) {
          finalStatus = status;
          return;
        }
        if ((finalStatus >= 400 && status === 200) || (finalStatus === 200 && status >= 400)) {
          finalStatus = 207;
        }
      }),
    );

    if (promises.length >= batchSize) {
      await Promise.all(promises);
      promises = [];
    }
  }

  const animalDataEvents = Object.values(allEvents).filter((eventType) => eventType.name === 'AnimalData');

  if (animalDataEvents[0].events.length) {
    promises.push(
      upserEvents(journey.id, journey.opportunityId, animalDataEvents, result).then((status) => {
        if (!finalStatus) {
          finalStatus = status;
          return;
        }
        if ((finalStatus >= 400 && status === 200) || (finalStatus === 200 && status >= 400)) {
          finalStatus = 207;
        }
      }),
    );
  }
  await Promise.all(promises);

  result.data = result;
  result.status = finalStatus;
  return result;
};

const upserEvents = (journeyId: string, opportunityId: string | undefined, events, result: any) => {
  return API.post(`${FIELD_URL}/field-service/data-collection`, {
    journeyId: journeyId,
    opportunityId: opportunityId,
    producerEvents: events,
  }).then((res) => {
    const savedEvents: EventsSuccess = res.data.eventsSuccess;
    for (const eventType of Object.keys(savedEvents)) {
      result.eventsSuccess[eventType] = result[eventType] ?? { ...savedEvents[eventType] };
      for (const event of Object.values(savedEvents[eventType]) as IEvents[]) {
        event.erroredEvent = ErroredEventMapping[eventType](event);
        result.eventsSuccess[eventType][event.id] = getEventWithoutNullValues(event);
      }
    }

    Object.keys(res.data.eventsFailed).map((eventType) => {
      result.eventsFailed[eventType] = result.eventsFailed[eventType] ?? {};
      for (const event of Object.values(res.data.eventsFailed[eventType][eventType]) as IEvents[]) {
        result.eventsFailed[eventType][event.id] = event;
      }
    });
    return res.status;
  });
};

const deleteEvents = async (eventsToDelete: IEventsToDelete[]) => {
  if (eventsToDelete.some((eventType) => eventType.eventIds.length)) {
    const body = {
      eventsToDelete: eventsToDelete.filter((e) => e.eventIds.length).map((event) => ({ ...event, fieldId: undefined })),
    };
    return await API.post(`${FIELD_URL}/field-service/data-collection/delete-events`, body);
  }
};

const getImplementationDate = async (journeyOrOpportunityId: string) => {
  return await API.get(`${FIELD_URL}/field-service/fields/practices/${journeyOrOpportunityId}`);
};

const updateImplementationDate = async (practicesToUpdate: IPracticeImplementationToUpdate[]) => {
  return await API.patch(`${FIELD_URL}/field-service/fields/practices`, { data: practicesToUpdate });
};

function getEventWithoutNullValues(event) {
  //null values are breaking the form
  const newEvent = {};
  Object.keys(event).forEach((k) => {
    // Convert N to string since it is the way we process it in the frontend
    if ((k === 'n' || k === 'p') && typeof event[k] === 'number') {
      event[k] = '' + event[k];
    }
    newEvent[k] = event[k] ? event[k] : '';
  });
  return newEvent;
}

async function getProducerFinishedYears(journeyId: string) {
  return API.get(`${FIELD_URL}/field-service/data-collection/producer-finished-years/${journeyId}`);
}

async function getProducerFinishedYearsHistory(journeyId: string) {
  return API.get(`${FIELD_URL}/field-service/data-collection/producer-finished-years-history/${journeyId}`);
}

async function saveProducerFinishedYears(journeyId: string, opportunityId: string, finishedYears: number[], practiceType: 'ROW_CROP' | 'PASTURE') {
  return API.post(`${FIELD_URL}/field-service/data-collection/producer-finished-years`, {
    journeyId,
    opportunityId,
    finishedYears,
    practiceType,
  });
}

function checkInSeasonTillageEvents(
  producerEvents: DCEventTypes,
  tillageTypes: Record<string, any>,
  eventsToDelete: IEventsToDelete[],
  fields: Field[],
) {
  const outOfSeasonTillages = {};
  const tillageEvents = Object.values(producerEvents['Tillage'].events) as IDCTillageEvent[];
  for (const tillage of tillageEvents) {
    const tillageDeleted = eventsToDelete?.find((ep) => ep.name === 'Tillage')?.eventIds?.includes(tillage.id);
    //Check if it's a valid Tillage event
    if (!tillage.tillageTypeId || !tillage.tillageDate || tillageDeleted) continue;
    const tillageType = tillageTypes.find((tillageType) => tillageType.id === tillage.tillageTypeId);
    if (!tillageType || tillageType.inSeason) continue;

    const tillageTime = new Date(tillage.tillageDate).getTime();
    const validPlantings = (Object.values(producerEvents['Planting'].events) as IDCPlantingEvent[]).filter(
      (e: IDCPlantingEvent) =>
        e.fieldId === tillage.fieldId && !!e.plantingDate && !eventsToDelete?.find((ep) => ep.name === 'Planting')?.eventIds?.includes(e.id),
    );

    //Check if there is any valid planting and harvest event
    for (const planting of validPlantings as IDCPlantingEvent[]) {
      const harvestWithinTillage = (Object.values(producerEvents['Harvest'].events) as IDCHarvestEvent[])
        .filter(
          (harvest) => harvest.plantingId === planting.id && !eventsToDelete?.find((ep) => ep.name === 'Harvest')?.eventIds?.includes(harvest.id),
        )
        .sort((h1, h2) => {
          const h1Time = h1.harvestDate ? new Date(h1.harvestDate).getTime() : 0;
          const h2Time = h2.harvestDate ? new Date(h2.harvestDate).getTime() : 0;
          return h1Time - h2Time;
        })
        ?.find((harvest) => {
          return (
            harvest.harvestDate && tillageTime >= new Date(planting.plantingDate).getTime() && tillageTime <= new Date(harvest.harvestDate).getTime()
          );
        })?.harvestDate;
      if (!harvestWithinTillage) continue;
      const tillageDate = getDateText(tillage.tillageDate);
      const plantingDate = getDateText(planting.plantingDate);
      const harvestDate = getDateText(harvestWithinTillage);
      const timeframeId = `${plantingDate} - ${harvestDate}`;
      outOfSeasonTillages[timeframeId]?.tillages
        ? outOfSeasonTillages[timeframeId].tillages.push(tillageDate)
        : (outOfSeasonTillages[timeframeId] = {
            plantingTimeFrame: [plantingDate, harvestDate],
            tillages: [tillageDate],
            fieldName: fields.find((fd) => fd.id === planting.fieldId)?.name,
          });
    }
  }
  return outOfSeasonTillages;
}

async function getModelInputDefaults() {
  return await API.get(`${FIELD_URL}/field-service/data-collection/model-input-defaults`);
}

async function blobToJson(value) {
  const resBlob = value.data;
  const resText = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('abort', reject);
    reader.addEventListener('error', reject);
    reader.addEventListener('loadend', () => {
      resolve(reader.result);
    });
    reader.readAsText(resBlob);
  });

  return JSON.parse(String(resText));
}

async function showErrorToastForInput(err, input: 'ghg' | 'mgmt') {
  const resData = await blobToJson(err.response);

  AgoToast.showToast({
    title: resData.title,
    message: resData.message,
    type: 'error',
    toastId: `${input}-file-generation-error-toast`,
  });
}

async function checkGHGStatus(generate: boolean): Promise<string> {
  return API.get(`${FIELD_URL}/field-service/fields/ghg-status/${generate}`).then((response) => {
    return response.data;
  });
}

export async function downloadGrowersGHGInputs() {
  let status = await checkGHGStatus(true);

  AgoToast.showToast({
    title: 'Request sent',
    message: `Acquiring data for GHG report. This may take a few minutes.`,
    type: 'info',
    toastId: `ghg-pending`,
  });

  while (status !== 'complete') {
    // add timeout before next execution of loop
    await new Promise((resolve) => setTimeout(resolve, 5000));

    if (status === 'error') {
      break;
    }

    status = await checkGHGStatus(false);
  }

  if (status === 'error') {
    AgoToast.showToast({
      title: 'Request failed',
      message: 'Failed to download inputs for GHG',
      type: 'error',
      toastId: 'ghg-file-generation-error-toast',
    });
    return;
  }
  return API.get(`${FIELD_URL}/field-service/fields/ghg-input/growers`)
    .then((response) => {
      downloadSignedUrl(response.data, `growers-GHG-zip_${currentDateISO()}.zip`);
    })
    .catch(async (err) => {
      await showErrorToastForInput(err, 'ghg');
    });
}

async function checkMgmtStatus(opportunityIds: string[], generate: boolean, taskId?: string): Promise<{ status: string; id: string }> {
  return API.post(`${FIELD_URL}/field-service/data-collection/dc-management-status/${generate}`, { opportunityIds, taskId }).then(
    (response) => response.data,
  );
}

export async function downloadGrowersMgmtCSVs(opportunityIds: string[]) {
  let mgmtStatus = await checkMgmtStatus(opportunityIds, true);

  AgoToast.showToast({
    title: 'Request sent',
    message: `Acquiring data for MGMT report. This may take a few minutes.`,
    type: 'info',
    toastId: 'mgmt-pending',
  });

  while (mgmtStatus.status !== 'complete') {
    // add timeout before next execution of loop
    await new Promise((resolve) => setTimeout(resolve, 5000));

    if (mgmtStatus.status === 'error') {
      break;
    }

    mgmtStatus = await checkMgmtStatus(opportunityIds, false, mgmtStatus.id);
  }

  if (mgmtStatus.status === 'error') {
    AgoToast.showToast({
      title: 'Request failed',
      message: 'Failed to download inputs for MGMT',
      type: 'error',
      toastId: 'mgmt-file-generation-error-toast',
    });
    return;
  }
  return API.get(`${FIELD_URL}/field-service/data-collection/dc-management-csv/${mgmtStatus.id}`)
    .then((response) => {
      downloadSignedUrl(response.data, `mgmt_data_${currentDateISO()}.zip`);
    })
    .catch(async (err) => {
      await showErrorToastForInput(err, 'mgmt');
    });
}

const getAllFieldGroups = async () => {
  return API.get(`${FIELD_URL}/field-service/groups`);
};

const getForagePlantSpecies = async (dominantPlant?: boolean, forage?: boolean) => {
  return API.get(`${FIELD_URL}/field-service/data-collection/forage/plant-species`, { dominantPlant, forage });
};

export const DataCollectionService = {
  getEventsConfiguration,
  getImplementationYear,
  upsertImplementationYear,
  getFarmInformation,
  upsertGrazingSeason,
  getStateLocationRelations,
  getModelInputDefaults,
  deleteEvents,
  saveEvents,
  treatedEventTypes,
  copyEventTypes,
  checkInSeasonTillageEvents,
  getAllAffectedEvents,
  getImplementationDate,
  updateImplementationDate,
  getAllFieldGroups,
  getProducerFinishedYears,
  getProducerFinishedYearsHistory,
  saveProducerFinishedYears,
  getForagePlantSpecies,
};
